import { storeToRefs } from 'pinia';
import { defineNuxtRouteMiddleware, navigateTo } from '#app/composables/router';
import { useAuth } from '#layers/access/public';
import { SETTINGS_QUERY_KEY } from '#layers/global-settings/constants/settings-overlay';
import { resolveBackdropWorkspaceSlug } from '#layers/workspace/public';

/**
 * Redirects the `/~/:section` deep link onto the backdrop workspace with the section opened as a
 * `?settings=` overlay (or to the no-workspace page). Resolving the destination here — rather than
 * via `navigateTo` inside the page `setup()` — settles the redirect before the page mounts, avoiding
 * the re-entrant `router.replace` during hydration that otherwise strands the user on `/~/…`.
 *
 * `replace: true` keeps the transient `/~/…` entry out of history so the overlay's `close()` (which
 * reads `history.state.back`) does not bounce back here and re-open the overlay.
 */
export default defineNuxtRouteMiddleware(async (to) => {
	const raw = to.params.section;
	const section = Array.isArray(raw) ? raw.join('/') : (raw ?? '');
	const { currentUser } = storeToRefs(useAuth());
	const slug = await resolveBackdropWorkspaceSlug(currentUser.value?.id);

	if (!slug) {
		return navigateTo({ name: 'no-workspace' }, { replace: true });
	}

	return navigateTo({
		name: 'workspace-home',
		params: { workspace: slug },
		query: section ? { [SETTINGS_QUERY_KEY]: section } : {},
		hash: to.hash,
	}, { replace: true });
});
